import { useForm } from '@tanstack/react-form';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { type ReactElement, type ReactNode } from 'react';
import {
  forEachObj as rForEachObj,
  isDeepEqual as rIsDeepEqual,
  isDefined as rIsDefined,
  isNot as rIsNot,
  omitBy as rOmitBy,
  pick as rPick,
} from 'remeda';
import {
  number as vNumber,
  parse as vParse,
  pipe as vPipe,
  string as vString,
  transform as vTransform,
} from 'valibot';

import { PendingState } from '@/components/pending-state.component';
import { SecondaryActionsIconButton } from '@/components/secondary-actions-icon-button.component';
import { SESSION_STORAGE } from '@/constants/storage';
import { theme } from '@/constants/theme';
import { useFormDraft } from '@/hooks/use-form-draft.hook';
import { CampaignRequestDistributionEnum, CampaignRequestStatusEnum, CampaignTypeEnum, CreativeTypeEnum } from '@/libs/openapi';
import {
  Badge,
  Breadcrumb,
  Breadcrumbs,
  MenuItem,
  PROGRESS_INDICATOR_MIN_DELAY_DURATION,
  TOAST_TIMEOUT,
  Text,
  minDelay,
  tryCatch,
  CopyStrokeIcon,
  ArchiveStrokeIcon,
  HelpCircleStrokeIcon,
} from '@/libs/serenity-ui';
import { RouterLink, RouterMenuItemLink } from '@/libs/tanstack-router';
import { auditLogQueries } from '@/resources/audit-log/audit-log.api';
import { campaignRequestMutations, campaignRequestQueries } from '@/resources/campaign-request/campaign-request.api';
import { toApiCampaignRequestFieldAdapter } from '@/resources/campaign-request/campaign-request.field-adapter';
import { campaignRequestPolicies } from '@/resources/campaign-request/campaign-request.policies';
import { organizationTeamPolicies } from '@/resources/organization-team/organization-team.policies';
import { CopyIdToolbarTextButton } from '@/routes/_main/_form/-components/copy-id-toolbar-text-button.component';
import { CampaignRequestForm, type CampaignFormValues } from '@/routes/_main/_form/campaigns/-components/campaign-request-form.component';
import { Resource } from '@/types/resource.type';
import { timezone } from '@/utils/timezone.util';

export const Route = createFileRoute('/_main/_form/campaigns/draft/$campaignId/')({
  component: RouteComponent,
  pendingComponent: PendingState,
  params: {
    parse: ({ campaignId }) => ({ campaignId: vParse(vPipe(vString(), vTransform(Number), vNumber()), campaignId) }),
    stringify: ({ campaignId }) => ({ campaignId: String(campaignId) }),
  },
  staticData: {
    form: {
      heading: HeadingComponent,
      toolbarLayout: 'full-width',
      pageType: 'Campaign',
      slots: {
        topBarComponent: TopBar,
        actionsComponent: ActionsComponent,
        statusComponent: StatusComponent,
        idComponent: IdComponent,
      },
    },
  },
  beforeLoad: async ({ context, params }) => {
    if (typeof (params.campaignId as unknown) !== 'number') {
      throw notFound({ routeId: '__root__' });
    }

    if (context.user && !context.can.campaignRequests.view()) {
      throw redirect({ to: '/' });
    }

    return {
      resource: Resource.campaignRequest as const,
      queryOptions: {
        detail: campaignRequestQueries.detail(params.campaignId),
      },
      can: {
        [Resource.campaignRequest as const]: rPick(campaignRequestPolicies.bindContext({ user: context.user.original, organization: context.organization?.original }), [ 'create', 'update', 'archive', 'unarchive' ]),
        [Resource.organizationTeam as const]: rPick(organizationTeamPolicies.bindContext({ user: context.user.original }), [ 'create' ]),
      },
    };
  },
  loader: async ({ context, params }) => {
    if (!context.user) {
      return;
    }

    const data = await context.queryClient.ensureQueryData(context.queryOptions.detail);

    if (data.status !== CampaignRequestStatusEnum.DRAFT) {
      throw redirect({ to: '/campaigns/$campaignId', params: { campaignId: params.campaignId }});
    }
  },
  head: ({ params }) => ({
    meta: [
      {
        title: `TrendMD | Draft campaign ${params.campaignId}`,
      },
    ],
  }),
});

function RouteComponent(): React.ReactElement {
  const routeParams = Route.useParams();
  const navigate = Route.useNavigate();
  const routeContext = Route.useRouteContext();

  const campaignRequestQuery = useSuspenseQuery(routeContext.queryOptions.detail);

  const updateMutation = useMutation(campaignRequestMutations.update);
  const submitMutation = useMutation(campaignRequestMutations.submit);

  const form = useForm({
    defaultValues: {
      campaignName: campaignRequestQuery.data.original.campaignName,
      customId: campaignRequestQuery.data.original.customId ?? '',
      targetingType: campaignRequestQuery.data.original.targetingType,
      npiFile: campaignRequestQuery.data.original.npiFile ? {
        id: campaignRequestQuery.data.original.npiFile.id,
        name: campaignRequestQuery.data.original.npiFile.name,
      } : undefined,
      creativeFile: campaignRequestQuery.data.original.creativeFile ? {
        id: campaignRequestQuery.data.original.creativeFile.id,
        name: campaignRequestQuery.data.original.creativeFile.name,
      } : undefined,
      specialtyTargeting: campaignRequestQuery.data.original.specialtyTargeting ?? '',
      geoTargeting: campaignRequestQuery.data.original.geoTargeting ?? '',
      totalBudget: campaignRequestQuery.data.transformed.forForm.totalBudget,
      cost: campaignRequestQuery.data.transformed.forForm.cost,
      campaignType: campaignRequestQuery.data.original.campaignType ?? CampaignTypeEnum.CPC,
      clickGoals: campaignRequestQuery.data.original.clickGoal ?? undefined,
      timezone: campaignRequestQuery.data.original.timezone ?? timezone.get(),
      startDate: campaignRequestQuery.data.meta.startDateRelativeToTimezone?.forFormValue,
      endDate: campaignRequestQuery.data.meta.endDateRelativeToTimezone?.forFormValue,
      distribution: campaignRequestQuery.data.original.distribution,
      monthlyBudget: campaignRequestQuery.data.transformed.forForm.monthlyBudget ?? {},
      ownerUserId: campaignRequestQuery.data.original.owner?.id,
      teamId: campaignRequestQuery.data.original.team?.id,
      ownerSearchInput: '',
      teamSearchInput: '',
    } as CampaignFormValues,
    onSubmit: async ({ value }) => {
      const originalTimezone = campaignRequestQuery.data.original.timezone ?? timezone.get();
      const originalStartDate = campaignRequestQuery.data.meta.startDateRelativeToTimezone?.forFormValue;
      const originalEndDate = campaignRequestQuery.data.meta.endDateRelativeToTimezone?.forFormValue;
      const originalCustomId = campaignRequestQuery.data.original.customId;
      const originalTotalBudget = campaignRequestQuery.data.transformed.forForm.totalBudget;
      const originalCost = campaignRequestQuery.data.transformed.forForm.cost;
      const originalMonthlyBudget = campaignRequestQuery.data.original.distribution === CampaignRequestDistributionEnum.MONTHLY
        ? campaignRequestQuery.data.transformed.forForm.monthlyBudget
        : undefined;
      const monthlyBudget = value.distribution === CampaignRequestDistributionEnum.MONTHLY
        ? value.monthlyBudget
        : undefined;

      const body = rOmitBy(toApiCampaignRequestFieldAdapter.transform({
        campaignName: value.campaignName !== campaignRequestQuery.data.original.campaignName ? value.campaignName : undefined,
        customId: (value.customId || undefined) !== originalCustomId ? (value.customId || undefined) : undefined,
        targetingType: value.targetingType !== campaignRequestQuery.data.original.targetingType ? value.targetingType : undefined,
        npiFileId: value.npiFile?.id !== campaignRequestQuery.data.original.npiFile?.id ? value.npiFile?.id : undefined,
        creativeFileId: value.creativeFile?.id !== campaignRequestQuery.data.original.creativeFile?.id ? value.creativeFile?.id : undefined,
        specialtyTargeting: (value.specialtyTargeting || undefined) !== (campaignRequestQuery.data.original.specialtyTargeting || undefined)
          ? (value.specialtyTargeting || undefined)
          : undefined,
        geoTargeting: (value.geoTargeting || undefined) !== (campaignRequestQuery.data.original.geoTargeting || undefined)
          ? (value.geoTargeting || undefined)
          : undefined,
        totalBudget: (value.totalBudget ?? 0) !== originalTotalBudget ? (value.totalBudget ?? 0) : undefined,
        cost: (value.cost ?? 0) !== originalCost ? (value.cost ?? 0) : undefined,
        timezone: value.timezone !== originalTimezone ? value.timezone : undefined,
        startDate: value.startDate !== originalStartDate && value.startDate ? value.startDate : undefined,
        endDate: value.endDate !== originalEndDate && value.endDate ? value.endDate : undefined,
        distribution: value.distribution !== campaignRequestQuery.data.original.distribution ? value.distribution : undefined,
        monthlyBudget: !rIsDeepEqual(monthlyBudget, originalMonthlyBudget) ? monthlyBudget : undefined,
        ownerUserId: value.ownerUserId !== campaignRequestQuery.data.original.owner?.id ? value.ownerUserId : undefined,
        teamId: value.teamId !== campaignRequestQuery.data.original.team?.id ? value.teamId : undefined,
        creativeType: CreativeTypeEnum.LINK !== campaignRequestQuery.data.original.creativeType ? CreativeTypeEnum.LINK : undefined,
      }), rIsNot(rIsDefined));

      const updateResponse = await tryCatch(minDelay(
        updateMutation.mutateAsync({ id: routeParams.campaignId, body }),
        PROGRESS_INDICATOR_MIN_DELAY_DURATION,
      ));

      if (updateResponse.error) {
        routeContext.toastQueue.add({ message: updateResponse.error.message, variant: 'error', actionProps: { children: 'Retry', onPress: () => form.handleSubmit() }}, { timeout: TOAST_TIMEOUT });
        return;
      }

      const submitResponse = await tryCatch(submitMutation.mutateAsync(routeParams.campaignId));

      routeContext.queryClient.invalidateQueries(campaignRequestQueries.all);
      routeContext.queryClient.invalidateQueries(auditLogQueries.all);

      clearDraft();

      if (submitResponse.error) {
        routeContext.toastQueue.add({ message: 'Submission failed, campaign saved as draft.', variant: 'warning' }, { timeout: TOAST_TIMEOUT });
      } else {
        routeContext.toastQueue.add(
          { message: 'Campaign was submitted for review.', variant: 'success' },
          { timeout: TOAST_TIMEOUT },
        );
        navigate({ to: '/campaigns/$campaignId', params: { campaignId: routeParams.campaignId }});
      }
    },
  });

  const { hasDraft, restoreDraft, clearDraft, cancelDraft } = useFormDraft<CampaignFormValues>({
    storageKey: SESSION_STORAGE.campaignRequestEditDraft(String(routeParams.campaignId)).key,
    subscribe: (cb) => form.store.subscribe(({ currentVal }) => !currentVal.isDefaultValue && cb(currentVal.values)),
    restoreValues: rForEachObj((value, key) => form.setFieldValue(key, value)),
  });

  const handleSaveAsDraft = async (): Promise<void> => {
    const errors = await form.validateField('campaignName', 'blur');
    if (errors.length > 0) {
      return;
    }

    const value = form.state.values;
    const { original, transformed, meta } = campaignRequestQuery.data;
    const originalTimezone = original.timezone ?? timezone.get();
    const originalStartDate = meta.startDateRelativeToTimezone?.forFormValue;
    const originalEndDate = meta.endDateRelativeToTimezone?.forFormValue;
    const originalCustomId = original.customId || undefined;
    const originalTotalBudget = transformed.forForm.totalBudget ?? 0;
    const originalCost = transformed.forForm.cost ?? 0;
    const originalMonthlyBudget = original.distribution === CampaignRequestDistributionEnum.MONTHLY
      ? transformed.forForm.monthlyBudget
      : undefined;
    const monthlyBudget = value.distribution === CampaignRequestDistributionEnum.MONTHLY
      ? value.monthlyBudget
      : undefined;
    const creativeType = value.creativeFile ? CreativeTypeEnum.LINK : undefined;

    const body = rOmitBy(
      toApiCampaignRequestFieldAdapter.transform({
        campaignName: value.campaignName !== original.campaignName ? value.campaignName : undefined,
        customId: (value.customId || undefined) !== originalCustomId ? (value.customId || undefined) : undefined,
        targetingType: value.targetingType !== original.targetingType ? value.targetingType : undefined,
        npiFileId: value.npiFile?.id !== original.npiFile?.id ? value.npiFile?.id : undefined,
        creativeFileId: value.creativeFile?.id !== original.creativeFile?.id ? value.creativeFile?.id : undefined,
        specialtyTargeting: (value.specialtyTargeting || undefined) !== (original.specialtyTargeting || undefined)
          ? (value.specialtyTargeting || undefined)
          : undefined,
        geoTargeting: (value.geoTargeting || undefined) !== (original.geoTargeting || undefined)
          ? (value.geoTargeting || undefined)
          : undefined,
        totalBudget: (value.totalBudget ?? 0) !== originalTotalBudget ? (value.totalBudget ?? 0) : undefined,
        cost: (value.cost ?? 0) !== originalCost ? (value.cost ?? 0) : undefined,
        timezone: value.timezone !== originalTimezone ? value.timezone : undefined,
        startDate: value.startDate !== originalStartDate && value.startDate ? value.startDate : undefined,
        endDate: value.endDate !== originalEndDate && value.endDate ? value.endDate : undefined,
        distribution: value.distribution !== original.distribution ? value.distribution : undefined,
        monthlyBudget: !rIsDeepEqual(monthlyBudget, originalMonthlyBudget) ? monthlyBudget : undefined,
        ownerUserId: value.ownerUserId !== original.owner?.id ? value.ownerUserId : undefined,
        teamId: value.teamId !== original.team?.id ? value.teamId : undefined,
        creativeType: creativeType !== original.creativeType ? creativeType : undefined,
      }),
      rIsNot(rIsDefined),
    );

    const response = await tryCatch(minDelay(updateMutation.mutateAsync({ id: routeParams.campaignId, body }), PROGRESS_INDICATOR_MIN_DELAY_DURATION));

    if (response.error) {
      routeContext.toastQueue.add({ message: response.error.message, variant: 'error' }, { timeout: TOAST_TIMEOUT });
      return;
    }

    clearDraft();
    routeContext.queryClient.invalidateQueries(campaignRequestQueries.all);
    routeContext.queryClient.invalidateQueries(auditLogQueries.all);
    routeContext.toastQueue.add({ variant: 'success', message: 'Saved as a draft. You can update and submit it for review later.' }, { timeout: TOAST_TIMEOUT });
  };

  return (
    <CampaignRequestForm
      canUpdate={routeContext.can.campaignRequest.update(campaignRequestQuery.data.original)}
      cancelDraft={cancelDraft}
      form={form}
      hasDraft={hasDraft}
      isSavePending={updateMutation.isPending}
      restoreDraft={restoreDraft}
      routeContext={routeContext}
      validationExcludeId={routeParams.campaignId}
      onSaveDraft={handleSaveAsDraft}
    />
  );
}

function TopBar (): ReactElement {
  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/campaigns">Campaigns</RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink to=".">Edit</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

function HeadingComponent(): ReactElement | string {
  const routeContext = Route.useRouteContext();

  const campaignRequestQuery = useSuspenseQuery(routeContext.queryOptions.detail);
  return campaignRequestQuery.data.original.campaignName;
}

function IdComponent(): ReactElement {
  const routeParams = Route.useParams();
  return <CopyIdToolbarTextButton id={routeParams.campaignId} />;
}

function StatusComponent(): ReactElement {
  return <Badge {...theme.badge['Draft']} size={32}>Draft</Badge>;
}

function ActionsComponent(): ReactElement {
  const routeParams = Route.useParams();
  return (
    <SecondaryActionsIconButton id={routeParams.campaignId}>
      <MenuActions />
    </SecondaryActionsIconButton>
  );
}

function MenuActions(): ReactNode {
  const routeParams = Route.useParams();
  const routeContext = Route.useRouteContext();
  const navigate = Route.useNavigate();

  const campaignRequestQuery = useSuspenseQuery(routeContext.queryOptions.detail);
  const isArchived = !!campaignRequestQuery.data.original.archivedAt;

  const archiveMutation = useMutation(campaignRequestMutations.archive);

  return (
    <>
      {routeContext.can.campaignRequest.create() && (
        <MenuItem onAction={() => navigate({ to: '/campaigns/duplicate/$campaignId', params: { campaignId: routeParams.campaignId }})}>
          <CopyStrokeIcon />
          <Text>Duplicate</Text>
        </MenuItem>
      )}
      {(isArchived ? routeContext.can.campaignRequest.unarchive : routeContext.can.campaignRequest.archive)(campaignRequestQuery.data.original) && (
        <MenuItem
          onAction={() => {
            archiveMutation.mutate({ id: routeParams.campaignId, archived: !isArchived }, {
              onSuccess: () => {
                routeContext.toastQueue.add({ message: isArchived ? 'Campaign unarchived.' : 'Campaign archived.', variant: 'success' }, { timeout: TOAST_TIMEOUT });
                navigate({ to: '/campaigns' });
              },
              onError: (error) => {
                routeContext.toastQueue.add({ message: error.message, variant: 'error' }, { timeout: TOAST_TIMEOUT });
              },
            });
          }}>
          <ArchiveStrokeIcon />
          <Text>{isArchived ? 'Unarchive' : 'Archive'}</Text>
        </MenuItem>
      )}
      <RouterMenuItemLink to="/campaigns/edit/guide">
        <HelpCircleStrokeIcon />
        <Text>Help</Text>
      </RouterMenuItemLink>
    </>
  );
}
